
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Category } from "@/lib/types";

export function useCategories() {
  const queryClient = useQueryClient();

  const fetchCategories = async (): Promise<Category[]> => {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("name");

    if (error) {
      toast.error(`Error fetching categories: ${error.message}`);
      throw error;
    }

    // Use proper type assertion to map database results to our Category type
    return (data || []).map(item => ({
      id: item.id,
      name: item.name,
      description: item.description || undefined,
      created_at: item.created_at,
      updated_at: item.updated_at
    }));
  };

  const createCategory = async (category: Omit<Category, "id">): Promise<Category> => {
    // Get the current user ID
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }
    
    const { data, error } = await supabase
      .from("categories")
      .insert([{
        name: category.name,
        description: category.description, 
        user_id: userId,
        created_at: category.created_at,
        updated_at: category.updated_at
      }])
      .select()
      .single();

    if (error) {
      toast.error(`Error creating category: ${error.message}`);
      throw error;
    }

    if (!data) {
      toast.error("Failed to retrieve the created category");
      throw new Error("Failed to retrieve the created category");
    }

    toast.success("Category created successfully");
    return {
      id: data.id,
      name: data.name,
      description: data.description || undefined,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };

  const updateCategory = async (category: Partial<Category> & { id: string }): Promise<Category> => {
    // Get the current user ID to verify ownership (optional extra check)
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      throw new Error("User not authenticated");
    }
    
    const updateData: Record<string, any> = {};
    if (category.name !== undefined) updateData.name = category.name;
    if (category.description !== undefined) updateData.description = category.description;
    if (category.updated_at) updateData.updated_at = category.updated_at;
    
    const { data, error } = await supabase
      .from("categories")
      .update(updateData)
      .eq('id', category.id)
      .select()
      .single();

    if (error) {
      toast.error(`Error updating category: ${error.message}`);
      throw error;
    }

    if (!data) {
      toast.error("Failed to retrieve the updated category");
      throw new Error("Failed to retrieve the updated category");
    }

    toast.success("Category updated successfully");
    return {
      id: data.id,
      name: data.name,
      description: data.description || undefined,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };

  const deleteCategory = async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("categories")
      .delete()
      .eq('id', id);

    if (error) {
      toast.error(`Error deleting category: ${error.message}`);
      throw error;
    }

    toast.success("Category deleted successfully");
  };

  const getCategoryById = async (id: string): Promise<Category> => {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .eq('id', id)
      .single();

    if (error) {
      toast.error(`Error fetching category: ${error.message}`);
      throw error;
    }

    if (!data) {
      toast.error("Category not found");
      throw new Error("Category not found");
    }

    return {
      id: data.id,
      name: data.name,
      description: data.description || undefined,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  };

  // Queries
  const useCategoriesQuery = () => useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const useCategoryQuery = (id: string) => useQuery({
    queryKey: ["categories", id],
    queryFn: () => getCategoryById(id),
    enabled: !!id,
  });

  // Mutations
  const useCreateCategoryMutation = () => useMutation({
    mutationFn: createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const useUpdateCategoryMutation = () => useMutation({
    mutationFn: updateCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const useDeleteCategoryMutation = () => useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  return {
    useCategoriesQuery,
    useCategoryQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
  };
}
